import { Banner, FaroText } from "@faro-lotv/flat-ui";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { Box, Link, SxProps } from "@mui/material";
import { SettingsTabs } from "@router/route-params";
import { subscriptionSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useMemo, useState } from "react";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { bannerBaseStyles } from "@utils/ui-utils";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { contactSupport } from "@utils/email-utils";
import { ContactSupportLocations } from "@custom-types/types";
import { generateSettingRoute } from "@router/router-helper";
import { sphereColors } from "@styles/common-colors";
import { DEFAULT_INPUT_FONT_SIZE, DEFAULT_BOLD_TEXT_FONT_WEIGHT } from "@styles/common-styles";

/** Defines the styles applied to links displayed in the banner */
const LinkStyles: SxProps = {
  color:"inherit",
  fontSize: DEFAULT_INPUT_FONT_SIZE,
  fontWeight: DEFAULT_BOLD_TEXT_FONT_WEIGHT,
  cursor: "pointer",
  textDecorationColor: sphereColors.gray800,
  textDecorationStyle: "solid",
  textDecoration: "underline",
};

/** Banner to show when a subscription limit is exceeded */
export function ExceededSubscriptionLimitsBanner(): JSX.Element | null {
  const { trackEvent } = useTrackEvent();
  const subscription = useAppSelector(subscriptionSelector);
  const [isDismissed, setIsDismissed] = useState(false);
  const { navigateToSettingsPage } = useAppNavigation();
  const { canViewSubscriptionDetails } = useHasUserValidRoleCompanyLevel();

  const subscriptionTabRoute = generateSettingRoute(subscription?.subject.id ?? "" , SettingsTabs.subscription);

  const shouldShowBanner = useMemo(() => {
    return subscription?.constraints.some(
      (constraint) => (constraint.data?.current ?? 0) > (constraint.data?.max ?? 0)
      )
      && !isDismissed
      && canViewSubscriptionDetails;
    }, [subscription?.constraints, isDismissed, canViewSubscriptionDetails]);

  const handleSubscriptionNavigation = useCallback(() => {
    trackEvent({
      name: WorkspaceEvents.viewSubscriptionPage,
    });
    navigateToSettingsPage({
      settingsTab: SettingsTabs.subscription,
      companyId: subscription?.subject.id ?? "",
    });
  }, [trackEvent, navigateToSettingsPage, subscription?.subject.id]);

  function handleContactSupportClick(e: React.MouseEvent): void {
    e.preventDefault();
    contactSupport({
      location: ContactSupportLocations.exceededSubscriptionLimitsBanner,
      trackEvent,
    });
  };

  if (!shouldShowBanner ||  window.location.pathname === subscriptionTabRoute) {
    return null;
  }

  return (
    <Box data-testid="exceed-subscription-limit-banner">
      <Banner
        variant="warning"
        action={{
          label: "View More",
          action: handleSubscriptionNavigation,
        }}
        onClose={() => setIsDismissed(true)}
        sx={{
          ...bannerBaseStyles,
          '& [data-name="exclamation-mark-circle-fill"]': {
            color: sphereColors.yellow650,
          },
        }}
      >
        <FaroText variant={"heading14"} sx={{ paddingRight: "16px"}}> Plan limit exceeded </FaroText>
        <FaroText variant={"bodyS"}> Your account currently exceeds the limits of your plan. Review which features you can reduce or contact {" "}
         <Link
        onClick={handleContactSupportClick}
        sx={LinkStyles}
        component="a"
        aria-label="Contact support"
          >
        sales/support
          </Link>
          {" "}to upgrade your plan.
          </FaroText>
      </Banner>
    </Box>
  );
}